import * as THREE from 'three'
import * as BufferGeometryUtils from 'three/examples/jsm/utils/BufferGeometryUtils.js'
import { AppObject } from '../types'

export function createTransformedGeometry(
  object: AppObject,
  scale: number,
  recenterIndividually: boolean = false
): THREE.BufferGeometry {
  let geometry = new THREE.BufferGeometry()

  if (object.mesh.attributes.position?.array) {
    geometry.setAttribute(
      'position',
      new THREE.BufferAttribute(new Float32Array(object.mesh.attributes.position.array), 3)
    )
  }
  if (object.mesh.attributes.normal?.array) {
    geometry.setAttribute('normal', new THREE.BufferAttribute(new Float32Array(object.mesh.attributes.normal.array), 3))
  }
  if (object.mesh.attributes.uv?.array) {
    geometry.setAttribute('uv', new THREE.BufferAttribute(new Float32Array(object.mesh.attributes.uv.array), 2))
  }
  // Only meaningful alongside a normal map, which is the one case that survives here.
  if (object.mesh.attributes.tangent?.array) {
    geometry.setAttribute(
      'tangent',
      new THREE.BufferAttribute(new Float32Array(object.mesh.attributes.tangent.array), 4)
    )
  }
  if (object.mesh.index?.array) {
    geometry.setIndex(new THREE.BufferAttribute(new Uint32Array(object.mesh.index.array), 1))
  }

  const hasNormals = !!geometry.attributes.normal

  // Only merge vertices when no normals are provided (e.g., STEP files without normals).
  // GLB files use duplicate vertices at hard edges for sharp shading — merging destroys that.
  if (!hasNormals) {
    geometry = BufferGeometryUtils.mergeVertices(geometry, 0.0001)
  }

  // Only re-center individual parts when exporting as separate files (ZIP)
  if (recenterIndividually) {
    geometry.computeBoundingBox()
    if (geometry.boundingBox) {
      const mergedCenter = new THREE.Vector3()
      geometry.boundingBox.getCenter(mergedCenter)

      const posAttr = geometry.getAttribute('position')
      for (let i = 0; i < posAttr.count; i++) {
        posAttr.setX(i, posAttr.getX(i) - mergedCenter.x)
        posAttr.setZ(i, posAttr.getZ(i) - mergedCenter.z)
        posAttr.setY(i, posAttr.getY(i) - geometry.boundingBox.min.y)
      }
      posAttr.needsUpdate = true
    }
  }

  if (!hasNormals) {
    geometry.computeVertexNormals()
  }

  // Scale to meters
  geometry.scale(scale, scale, scale)
  return geometry
}
